import { useState } from "react";
import { Check, ChevronsUpDown } from "lucide-react";
import { type Identifier, useListContext, useTranslate } from "ra-core";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { cn } from "@/lib/utils";

import { useAllTags } from "../tags/useAllTags";
import {
  type TagMatchMode,
  readTagFilter,
  writeTagFilter,
} from "./tagFilterValue";

/**
 * The tag filter, as one searchable control rather than one row per tag.
 *
 * There are 31 tags in production and the sidebar rendered a row for each, so
 * the panel alone was taller than the screen and pushed everything under it out
 * of view. See LCATOMICZO-32.
 *
 * The list lives in a Radix Popover, which renders through a portal, so it
 * overlays the page instead of nesting a scroll region inside the sidebar's own
 * scroll area.
 */
export const TagFilterSelect = () => {
  const [open, setOpen] = useState(false);
  const translate = useTranslate();
  const { filterValues, setFilters } = useListContext();
  const { data: tags } = useAllTags();

  const { ids, mode } = readTagFilter(filterValues);
  const selected = new Set(ids.map(String));

  const apply = (nextIds: Identifier[], nextMode: TagMatchMode) =>
    setFilters(writeTagFilter(filterValues, nextIds, nextMode), []);

  const toggle = (id: Identifier) =>
    apply(
      selected.has(String(id))
        ? ids.filter((i) => String(i) !== String(id))
        : [...ids, id],
      mode,
    );

  const chosen = (tags ?? []).filter((tag) => selected.has(String(tag.id)));

  return (
    <div className="w-full flex flex-col gap-1.5">
      {/* Outside the popover and always shown, so what several tags mean is
          visible without opening anything. */}
      <div className="flex items-center gap-1">
        <span className="text-xs text-muted-foreground">
          {translate("resources.contacts.filters.tags_match", { _: "Match" })}
        </span>
        {(["any", "all"] as const).map((option) => (
          <Button
            key={option}
            type="button"
            size="sm"
            variant={mode === option ? "secondary" : "ghost"}
            className="h-6 px-2 text-xs cursor-pointer"
            aria-pressed={mode === option}
            onClick={() => apply(ids, option)}
          >
            {translate(`resources.contacts.filters.tags_match_${option}`, {
              _: option,
            })}
          </Button>
        ))}
      </div>

      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            variant={chosen.length > 0 ? "highlight" : "outline"}
            role="combobox"
            aria-expanded={open}
            className="w-full h-auto min-h-9 md:min-h-8 justify-between gap-2 p-2 font-normal cursor-pointer"
          >
            {chosen.length === 0 ? (
              <span className="text-muted-foreground text-sm md:text-xs">
                {translate("resources.contacts.filters.tags_placeholder", {
                  _: "Filter by tag…",
                })}
              </span>
            ) : (
              <span className="flex flex-wrap items-center gap-1">
                {chosen.map((tag) => (
                  <Badge
                    key={tag.id}
                    variant="secondary"
                    className="text-black text-sm md:text-xs font-normal"
                    style={{ backgroundColor: tag.color }}
                  >
                    {tag.name}
                  </Badge>
                ))}
              </span>
            )}
            <ChevronsUpDown className="opacity-50 shrink-0" />
          </Button>
        </PopoverTrigger>

        <PopoverContent
          align="start"
          className="w-[var(--radix-popover-trigger-width)] min-w-56 p-0"
        >
          <Command>
            <CommandInput
              placeholder={translate("resources.contacts.filters.tags_search", {
                _: "Search tags…",
              })}
            />
            <CommandList>
              <CommandEmpty>
                {translate("resources.contacts.filters.tags_empty", {
                  _: "No tag found.",
                })}
              </CommandEmpty>
              <CommandGroup>
                {(tags ?? []).map((tag) => (
                  <CommandItem
                    key={tag.id}
                    value={tag.name}
                    onSelect={() => toggle(tag.id)}
                    className="cursor-pointer"
                  >
                    <Check
                      className={cn(
                        "shrink-0",
                        selected.has(String(tag.id))
                          ? "opacity-100"
                          : "opacity-0",
                      )}
                    />
                    <Badge
                      variant="secondary"
                      className="text-black text-xs font-normal"
                      style={{ backgroundColor: tag.color }}
                    >
                      {tag.name}
                    </Badge>
                  </CommandItem>
                ))}
              </CommandGroup>
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
    </div>
  );
};
